import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { TextToSpeech } from 'tts-react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Timer from '../Timer/Timer';
import MuiAlert from '@mui/material/Alert';
import ReactHtmlParser from 'react-html-parser';
import { useNavigate, useParams } from 'react-router-dom';
import { useConnection, useUserInfo } from '../../connection/Application';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Swal from 'sweetalert2';
import { CorrectDnd, Dnd } from '../Dnd';
import { CorrectFillText } from '../fillText';
import { ClickWordBank } from '../Dnd/WordBank';
import { QuestionNumber } from '../QuestionNumber/QuestionNumber';

const useStyles = makeStyles({
  ttscls: {
    '& div': {
      alignItems: 'flex-start !important',
      '& aside': {
        margin: '0px !important',
      },
    },
  },
});

export const TTS: FC<{ children: any }> = (props) => {
  const classes = useStyles();
  return (
    <div className={classes.ttscls}>
      <TextToSpeech
        align='horizontal'
        allowMuting={false}
        markBackgroundColor='#55AD66'
        markColor='white'
        markTextAsSpoken
        position='leftCenter'
        rate={1}
        size='small'
        volume={1}>
        <div style={{ paddingTop: '0px' }}>{props.children}</div>
      </TextToSpeech>
    </div>
  );
};

// Maps a stored answer value to the text we want read aloud (e.g. boolean -> "True"/"False").
const spokenAnswer = (type: string, value?: string) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  if (type === 'boolean') {
    return value === 'true' ? 'True' : 'False';
  }
  return value + '';
};

// Renders a value as a visible, read-aloud (TTS) chip. Empty values render nothing.
const TTSText: FC<{ text?: string, hideText?: boolean }> = ({ text, hideText }) => {
  if (!text) {
    return null;
  }
  return (
    <div style={{ marginTop: hideText ? '30px' :'10px' }}>
      <TTS>
        <span style={{ fontSize: '16px', display: hideText ? 'none' :'inline-block', }}>{text}</span>
      </TTS>
    </div>
  );
};

const AnswerContent: FC<{ type: 'text' | 'richText'; text?: string; image?: string }> = ({
  type,
  text,
  image,
}) => {
  return (
    <Grid container spacing={2}>
      {text && (
        <Grid item sm={12}>
          {type === 'text' ? (
            <TTS>
              <div style={{ marginTop: '0px' }}>
                <p style={{ fontSize: '18px', display: 'block' }}>{text}</p>
              </div>
            </TTS>
          ) : (
            <TTS>
              <div style={{ marginTop: '0px', fontSize: '18px', display: 'block'  }}>{ReactHtmlParser(text)}</div>
            </TTS>
          )}
        </Grid>
      )}
      {image && (
        <Grid item sm={12}>
          <img
            src={process.env.REACT_APP_File_URL + image}
            alt='My Team'
            style={{ maxWidth: '100%', maxHeight: '100%', marginLeft: '50px' }}
          />
        </Grid>
      )}
    </Grid>
  );
};

const Answer: FC<{
  number: number;
  type: any;
  cType: 'text' | 'richText';
  text?: string;
  image?: string;
  value?: number | boolean;
}> = ({ number, type, cType, text, image, value }) => {
  return (
    <Grid item sm={6}>
      <div style={{ display: 'flex', margin: '10px 0', alignItems: 'flex-start' }}>
        <Typography style={{ margin: '5px 10px', fontSize: '15px', marginTop: '2px !important' }} fontWeight={700}>
          {number})
        </Typography>
        <FormControlLabel
          value={value}
          control={type}
          style={{ alignItems: 'flex-start' }}
          label={<AnswerContent type={cType} text={text} image={image} />}
        />
      </div>
    </Grid>
  );
};

const QuestionHead: FC<{
  number: number;
  type: 'text' | 'richText' | 'math';
  text?: string;
  image?: string;
  lable1?: string;
  lable2?: string;
  previouslyWrong?: boolean;
}> = ({ number, type, text, image, lable1, lable2, previouslyWrong }) => {
  const operator =
    (text ?? '').split('+').length > 1
      ? '+'
      : (text ?? '').split('-').length > 1
      ? '-'
      : (text ?? '').split('*').length > 1
      ? '*'
      : (text ?? '').split('/').length > 1
      ? '÷'
      : '';
  const ss =
    operator === '+'
      ? (text ?? '').split('+')
      : operator === '-'
      ? (text ?? '').split('-')
      : operator === '*'
      ? (text ?? '').split('*')
      : operator === '÷'
      ? (text ?? '').split('/')
      : (text ?? '').split('');
  return (
    <div style={{ display: 'flex', margin: '20px 0', alignItems: 'flex-start' }}>
      <QuestionNumber number={number} highlight={previouslyWrong} />
      <div style={{ flex: 1 }}>
        {text && (
          <>
            <TTS>
              {type === 'text' ? (
                <p
                  style={{
                    fontWeight: 500,
                    fontSize: '18px',
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                  }}>
                  {text}
                </p>
              ) : type === 'math' ? (
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 600,
                    display: 'none',
                    fontSize: '18px',
                    margin: '0px !important',
                  }}>
                  {text}
                </Typography>
              ) : (
                <Typography
                  style={{
                    fontSize: '18px',
                    margin: '0px !important',
                    display: 'block',
                    fontWeight: 500,
                  }}>
                  {ReactHtmlParser(text)}
                </Typography>
              )}
            </TTS>
            {type === 'math' && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ paddingRight: '70%', textAlign: 'end', width: '100%' }}>
                  <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '18px' }}>
                    {ss[0]}
                  </Typography>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    paddingRight: 'calc( 70% - 25px )',
                    width: '100%',
                    justifyContent: 'flex-end',
                  }}>
                  <div style={{}}>
                    <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '18px' }}>
                      {ss[1]}
                    </Typography>
                  </div>
                  <div style={{ width: '25px', textAlign: 'center' }}>
                    <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '18px' }}>
                      {operator}
                    </Typography>
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid',
                    marginLeft: '10%',
                    width: 'calc( 20% + 25px )',
                  }}></div>
              </div>
            )}
          </>
        )}
        {lable1 && (
          <div>
            <h5
              color={'primary'}
              style={{ marginBottom: '20px', marginTop: '0px', fontSize: '12px' }}>
              {lable1}
            </h5>
          </div>
        )}
        {image && (
          <Grid item sm={12}>
            <img
              src={image}
              alt='My Team'
              style={{ maxWidth: '95%', maxHeight: '55%', marginLeft: '50px' }}
            />
          </Grid>
        )}
        {lable2 && (
          <h5
            color={'success'}
            style={{
              marginBottom: '20px',
              marginLeft: '20px',
              marginTop: '0px',
              fontSize: '12px',
            }}>
            {lable2}
          </h5>
        )}
      </div>
    </div>
  );
};

const Alert = React.forwardRef(function Alert(props, ref) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return <MuiAlert ref={ref} elevation={6} variant='filled' {...props} />;
});

// const transform = (node: any, index: number) => {
//   if (node.type === 'text' && node.data.includes('###BLANK###')) {
//     return (
//       <>
//         <>
//           {ReactHtmlParser(node.data.slice(0, node.data.indexOf('###BLANK###')), {
//             transform: transform,
//           })}
//         </>
//         <DroppableContainer
//           items={['1']}
//           id={index + '-2'}
//           isCorrect={false}
//           allBlanksEmpty={true}
//           style={{
//             height: '40px',
//           }}>
//           <SortableItem1
//             key={'sortable-item-'}
//             isMap={false}
//             id={index + '-1'}
//             taskId={'1'}
//             isCorrect={false}
//           />
//         </DroppableContainer>
//         <>
//           {ReactHtmlParser(
//             node.data.slice(node.data.indexOf('###BLANK###') + 11, node.data.length),
//             { transform: transform },
//           )}
//         </>
//       </>
//     );
//   }
// };


const findMinWidth = (
  solutions: any[],
) => {
  const s = [];

  for (const sElement of solutions) {
    const text = sElement.value;
    if (document.getElementById('text101') !== null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      document.getElementById('text101').innerHTML = text;
      s.push({
        ...sElement,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        width: document.getElementById('text101').getClientRects()[0].width + 1,
      });
    }
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  document.getElementById('text101').innerHTML = '';
  return s.sort((a: any, b: any) => b.width - a.width)?.[0];
};

const Question: FC<{
  question: any;
  questionId: string;
  setMark: (x: number) => void;
  setCurrentCount: (x: number) => void;
  initialDoneQuestionCount: number;
  currentCount: number;
  setCurrent: (x: number) => void;
  setIndexCurrent: (x: number) => void;
  current: number;
  indexCurrent: number;
  setQCount: (x: number, r: boolean) => void;
  setDoneQuestionCount: (x: number) => void;
  qCount: number;
  mark: number;
  time: number;
  answerLength: number;
  xAnswer?: string[];
  previouslyWrong?: boolean;
  previousQuestionNumber?: number | null;
  attemptedCount?: number;
  setAttemptedCount?: (x: number) => void;
  reload?: () => Promise<void>;
}> = ({
  question,
  questionId,
  setMark,
  setCurrentCount,
  initialDoneQuestionCount,
  currentCount,
  setCurrent,
  setIndexCurrent,
  current,
  indexCurrent,
  setQCount,
  setDoneQuestionCount,
  qCount,
  mark,
  time,
  answerLength,
  xAnswer,
  previouslyWrong,
  previousQuestionNumber,
  attemptedCount,
  setAttemptedCount,
  reload,
}) => {
  const navigator = useNavigate();
  // Fresh questions count up from how many this user has attempted in the
  // subtitle. A question that comes back after a wrong answer shows the number
  // it had the first time (stored server-side) inside the red badge instead of
  // consuming a new one.
  // The number is fixed when a question first appears and stays put while its
  // review screen is up, even though the attempted count already moved on.
  const numberForQuestionRef = useRef<{ questionId?: string; number: number }>({ number: 1 });
  if (numberForQuestionRef.current.questionId !== questionId) {
    const freshQuestionNumber =
      typeof attemptedCount === 'number' ? attemptedCount + 1 : current + 1 + initialDoneQuestionCount;
    numberForQuestionRef.current = {
      questionId,
      number: previouslyWrong && previousQuestionNumber ? previousQuestionNumber : freshQuestionNumber,
    };
  }
  const questionNumber = numberForQuestionRef.current.number;
  const [open, setOpen] = useState(false);
  const [open1, setOpen1] = useState(false);
  const [wrong, setWrong] = useState(false);
  // Mirrors `wrong` synchronously. State updates are batched, so the tail of the
  // submit handler would still read the stale value when it decides whether this
  // answer was correct.
  const answeredWrongRef = useRef(false);
  // True while an answer request is in flight or while we are moving to the next
  // question. Enter key auto-repeat and "button click + document keydown" both
  // deliver several triggers for one user action; only the first may act.
  const busyRef = useRef(false);
  const [open2, setOpen2] = useState(false);
  const [answerWidth, setAnswerWidth] = useState(18 * answerLength);
  const [loading, setLoading] = useState<boolean>(false);
  const [loading1, setLoading1] = useState<boolean>(false);
  const [correctAnswer, setCorrectAnswer] = React.useState<string[]>();
  const [correctAnswer1, setCorrectAnswer1] = React.useState<string[]>();
  const [reviewIsCorrectByBlank, setReviewIsCorrectByBlank] = useState<boolean[] | undefined>();
  const [needToReload, setNeedToReload] = useState(false);
  const [danswers, setDanswers] = useState<any>();
  const connection = useConnection();
  const dndref = useRef<any>([]);

  const handleClick = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const markWrong = useCallback(() => {
    answeredWrongRef.current = true;
    setWrong(true);
  }, [setWrong]);

  useEffect(() => {
    const text = xAnswer?.[0];
    const probe = document.getElementById('text101');
    if (probe !== null) {
      probe.innerHTML = text ?? '';
      setAnswerWidth((probe.scrollWidth ?? 0) + 20);
      probe.innerHTML = '';
    }
  }, [answerLength]);

  // Whatever review state the previous question left behind must never bleed
  // into the next one (disabled inputs + "Correct Answer" box before answering).
  useEffect(() => {
    answeredWrongRef.current = false;
    setWrong(false);
    setCorrectAnswer1(undefined);
    setDanswers(undefined);
    setReviewIsCorrectByBlank(undefined);
    setLoading1(false);
  }, [questionId]);

  useEffect(() => {
    const a = [];
    let i = -1;
    while ((i = question.question.indexOf('###BLANK###', i + 1)) >= 0) {
      a.push(i);
    }
    while ((i = question.question.indexOf('###BLANK1###', i + 1)) >= 0) {
      a.push(i);
    }
    setCorrectAnswer(a.length === 0 ? [''] : [...new Array(a.length)].map(() => ''));
    setCorrectAnswer1(undefined);
    setReviewIsCorrectByBlank(undefined);
  }, [question.question]);

  const normalizeAnswer = useCallback((value?: string) => {
    return ((value ?? '') + '').trim().toLowerCase();
  }, []);

  const isSemanticTextCorrect = useCallback(
    (responseBody: any, givenAnswer?: string) => {
      if (typeof responseBody?.llm?.isCorrect === 'boolean') {
        return !!responseBody.llm.isCorrect;
      }
      return normalizeAnswer(responseBody?.q?.[0]) === normalizeAnswer(givenAnswer);
    },
    [normalizeAnswer],
  );

  const mergeReviewAnswers = useCallback(
    (answers: string[], submittedAnswers: string[] | undefined, isCorrectByBlank: boolean[]) => {
      return answers.map((answer: string, index: number) =>
        isCorrectByBlank[index] ? submittedAnswers?.[index] ?? answer : answer,
      );
    },
    [],
  );

  const handleClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }

      setOpen(false);
    },
    [setOpen],
  );

  const handleClick1 = useCallback(() => {
    setOpen1(true);
  }, [setOpen1]);

  const handleClose1 = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }

      setOpen1(false);
    },
    [setOpen1],
  );

  const hasAnswer = useCallback((answers?: string[]) => {
    return (answers ?? []).some((a) => ((a ?? '') + '').trim() !== '');
  }, []);

  const resetReview = useCallback(() => {
    setCorrectAnswer(undefined);
    setCorrectAnswer1(undefined);
    setDanswers(undefined);
    setReviewIsCorrectByBlank(undefined);
  }, []);

  const goNext = useCallback(() => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setLoading(true);
    setTimeout(async () => {
      try {
        if (needToReload) {
          setNeedToReload(false);
          await reload?.();
        } else {
          setCurrent(current + 1);
          setIndexCurrent(indexCurrent + 1);
        }
      } finally {
        resetReview();
        setWrong(false);
        setLoading(false);
        setLoading1(false);
        busyRef.current = false;
      }
    }, 200);
  }, [needToReload, reload, current, indexCurrent, setCurrent, setIndexCurrent, resetReview]);

  const submit = useCallback(
    (id: string) => {
      if (busyRef.current || wrong) {
        return;
      }
      if (question.questionType !== 'plain' && !hasAnswer(correctAnswer)) {
        setOpen2(true);
        return;
      }
      busyRef.current = true;
      answeredWrongRef.current = false;
      if (question.questionType === 'plain') {
        setLoading1(true);
        setCurrent(current + 1);
        setIndexCurrent(indexCurrent + 1);
        setLoading(false);
        setCorrectAnswer([]);
        handleClick();
      }

      if (question.questionType === 'drag' || question.questionType === 'fillText') {
        setDanswers(correctAnswer);
        setLoading1(true);
      } else {
        setLoading(true);
      }

      connection
        .post(`question/answer/${id}/${time}`, {
          correctAnswer: correctAnswer ?? [''],
          questionNumber,
        })
        .then(async (e) => {
          const responseBody = e.response.body;
          // The server stores the verdict; when it tells us, trust it so the UI and
          // the database never disagree about whether this answer was right.
          const serverWrong: boolean | undefined =
            typeof responseBody?.wrong === 'boolean' ? responseBody.wrong : undefined;
          const isRight = (localVerdict: boolean) =>
            serverWrong === undefined ? localVerdict : !serverWrong;
          setReviewIsCorrectByBlank(undefined);
          if (typeof responseBody?.doneQuestionCount === 'number') {
            setDoneQuestionCount(responseBody.doneQuestionCount);
          }
          if (typeof responseBody?.attemptedCount === 'number') {
            setAttemptedCount?.(responseBody.attemptedCount);
          }
          if (question.questionType === 'text' || question.questionType === 'textArea') {
            setCorrectAnswer1(responseBody.q);
            setQCount(qCount + 1, e.response.body.reload);
            if (isRight(isSemanticTextCorrect(responseBody, correctAnswer?.[0]))) {
              // setMark((mark * current + 100) / (current + 1));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setLoading(false);
              setCorrectAnswer([]);
              handleClick();
            } else {
              markWrong();
              // setMark((mark * current) / (current + 1));
              setLoading(false);
              handleClick1();
            }
          } else if (question.questionType === 'multipleSelect') {
            setCorrectAnswer1(responseBody.q);
            setQCount(qCount + 1, e.response.body.reload);
            const cc = correctAnswer?.filter((e) => e !== '');
            if (
              isRight(
                e.response.body.q.length === (cc ?? []).length &&
                  !(cc ?? [])
                    .map((s) => !!e.response.body.q.find((d: string) => d === s))
                    .find((m) => !m),
              )
            ) {
              setMark((mark * currentCount + 100) / (currentCount + 1));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setCurrentCount(currentCount + 1);
              setCorrectAnswer([]);
              setLoading(false);
              handleClick();
            } else {
              setMark((mark * currentCount) / (currentCount + 1));
              setCurrentCount(currentCount + 1);
              markWrong();
              setLoading(false);
              handleClick1();
            }
          } else if (question.questionType === 'boolean') {
            setCorrectAnswer1(responseBody.q);
            setQCount(qCount + 1, e.response.body.reload);
            if (isRight(e.response.body.q[0] + '' === correctAnswer?.[0] + '')) {
              setMark((mark * currentCount + 100) / (currentCount + 1));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setCurrentCount(currentCount + 1);
              setCorrectAnswer([]);
              setLoading(false);
              handleClick();
            } else {
              setMark((mark * currentCount) / (currentCount + 1));
              setCurrentCount(currentCount + 1);
              markWrong();
              setLoading(false);
              handleClick1();
            }
          } else if (question.questionType === 'number' || question.questionType === 'math') {
            setCorrectAnswer1(responseBody.q);
            setQCount(qCount + 1, e.response.body.reload);
            if (isRight(+e.response.body.q[0] === +(correctAnswer?.[0] ?? 0))) {
              setMark((mark * currentCount + 100) / (currentCount + 1));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setCurrentCount(currentCount + 1);
              setCorrectAnswer([]);
              setLoading(false);
              handleClick();
            } else {
              setMark((mark * currentCount) / (currentCount + 1));
              setCurrentCount(currentCount + 1);
              markWrong();
              setLoading(false);
              handleClick1();
            }
          } else if (question.questionType === 'drag') {
            const isCorrectByBlank: boolean[] = Array.isArray(e.response.body.isCorrectByBlank)
              ? e.response.body.isCorrectByBlank
              : e.response.body.q.map((a: string, i: number) => a === correctAnswer?.[i]);
            setReviewIsCorrectByBlank(isCorrectByBlank);
            setCorrectAnswer1(mergeReviewAnswers(responseBody.q, correctAnswer, isCorrectByBlank));
            dndref.current[0].submit(e.response.body.q, correctAnswer, isCorrectByBlank);
            const mark1 = isCorrectByBlank.filter(Boolean).length;
            const totalBlankCount = isCorrectByBlank.length || e.response.body.q.length || 1;
            setQCount(qCount + totalBlankCount, e.response.body.reload);
            if (mark1 === totalBlankCount) {
              setLoading(true);
              setMark((mark * currentCount + mark1 * 100) / (currentCount + totalBlankCount));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setCurrentCount(currentCount + totalBlankCount);
              setCorrectAnswer([]);
              setDanswers(undefined);
              setTimeout(() => {
                setLoading(false);
                setLoading1(false);
                handleClick();
              }, 600);
            } else {
              setDanswers(correctAnswer);
              setMark((mark * currentCount + mark1 * 100) / (currentCount + totalBlankCount));
              setCurrentCount(currentCount + totalBlankCount);
              markWrong();
              setLoading(false);
              setLoading1(false);
              handleClick1();
            }
          } else if (question.questionType === 'fillText') {
            const isCorrectByBlank: boolean[] = Array.isArray(e.response.body.isCorrectByBlank)
              ? e.response.body.isCorrectByBlank
              : e.response.body.q.map(
                  (a: string, i: number) =>
                    (a + '').trim().toLowerCase() ===
                    ((correctAnswer?.[i] ?? '') + '').trim().toLowerCase(),
                );
            setReviewIsCorrectByBlank(isCorrectByBlank);
            setCorrectAnswer1(mergeReviewAnswers(responseBody.q, correctAnswer, isCorrectByBlank));
            dndref.current[1].submit(e.response.body.q, correctAnswer, isCorrectByBlank);
            const mark1 = isCorrectByBlank.filter(Boolean).length;
            const totalBlankCount = isCorrectByBlank.length || e.response.body.q.length || 1;
            setQCount(qCount + totalBlankCount, e.response.body.reload);
            if (mark1 === totalBlankCount) {
              setLoading(true);
              setMark((mark * currentCount + mark1 * 100) / (currentCount + totalBlankCount));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setCurrentCount(currentCount + totalBlankCount);
              setCorrectAnswer([]);
              setDanswers(undefined);
              setTimeout(() => {
                setLoading(false);
                setLoading1(false);
                handleClick();
              }, 600);
            } else {
              setDanswers(correctAnswer);
              setMark((mark * currentCount + mark1 * 100) / (currentCount + totalBlankCount));
              setCurrentCount(currentCount + totalBlankCount);
              markWrong();
              setLoading(false);
              setLoading1(false);
              handleClick1();
            }
          } else if (question.questionType === 'new-select') {
            setCorrectAnswer1(responseBody.q);
            setQCount(qCount + 1, e.response.body.reload);
            const correct = (e.response.body.q[0] + '' + '').trim().toLowerCase();
            const given = ((correctAnswer?.[0] ?? '') + '').trim().toLowerCase();
            if (isRight(correct === given)) {
              setMark((mark * currentCount + 100) / (currentCount + 1));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setCurrentCount(currentCount + 1);
              setCorrectAnswer([]);
              setLoading(false);
              handleClick();
            } else {
              setMark((mark * currentCount) / (currentCount + 1));
              setCurrentCount(currentCount + 1);
              markWrong();
              setLoading(false);
              handleClick1();
            }
          } else {
            setCorrectAnswer1(responseBody.q);
            setQCount(qCount + 1, e.response.body.reload);
            if (isRight(e.response.body.q[0] + '' === (correctAnswer?.[0] ?? '') + '')) {
              setMark((mark * currentCount + 100) / (currentCount + 1));
              setCurrent(current + 1);
              setIndexCurrent(indexCurrent + 1);
              setCurrentCount(currentCount + 1);
              setCorrectAnswer([]);
              setLoading(false);
              handleClick();
            } else {
              setMark((mark * currentCount) / (currentCount + 1));
              setCurrentCount(currentCount + 1);
              markWrong();
              setLoading(false);
              handleClick1();
            }
          }

          if (e.response.body.timeOut) {
            await Swal.fire({
              title: 'Warning!',
              text: 'Your Free Trial Time Is Over. Please Try Again Next Day',
              icon: 'warning',
              confirmButtonText: 'Ok',
            });
            navigator('/');
          } else if (e.response.body.reload) {
            if (answeredWrongRef.current) {
              // The review screen is up; refetch when the user clicks through it.
              setNeedToReload(true);
            } else {
              // Correct answer, so no review screen will follow to trigger the
              // refetch. Pull the re-due wrong questions in now, otherwise they
              // stay invisible until the user happens to miss something.
              setLoading(true);
              await reload?.();
              setCorrectAnswer(undefined);
              setCorrectAnswer1(undefined);
              setDanswers(undefined);
              setReviewIsCorrectByBlank(undefined);
              setLoading(false);
              setLoading1(false);
            }
          }
          busyRef.current = false;
        })
        .catch(() => {
          busyRef.current = false;
          setWrong(false);
          setLoading(false);
          setLoading1(false);
        });
    },
    [
      correctAnswer,
      connection,
      time,
      wrong,
      question,
      current,
      indexCurrent,
      qCount,
      mark,
      currentCount,
      reload,
      navigator,
      hasAnswer,
      markWrong,
      handleClick,
      handleClick1,
      isSemanticTextCorrect,
      mergeReviewAnswers,
      setCurrent,
      setIndexCurrent,
      setCurrentCount,
      setQCount,
      setDoneQuestionCount,
      setMark,
      questionNumber,
      setAttemptedCount,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.keyCode !== 13) {
        return;
      }
      // Holding Enter delivers a keydown every ~30ms; only the first press counts.
      if (e.repeat) {
        return;
      }
      // A focused button already handles Enter through its own click event.
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'BUTTON' || target?.closest?.('button')) {
        return;
      }
      if (wrong) {
        goNext();
      } else {
        submit(questionId);
      }
    };
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [wrong, questionId, submit, goNext]);

  return (
    <>
      {loading ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            padding: '100px',
            display: 'flex',
            justifyContent: 'center',
          }}>
          <CircularProgress />
        </div>
      ) : (
        <div className={'ql-editor'}>
          {loading1 && (
            <div
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                padding: '100px',
                display: 'flex',
                justifyContent: 'center',
              }}>
              <CircularProgress />
            </div>
          )}
          <FormControl style={{ width: '100%', opacity: loading1 ? 0.5 : 1 }}>
            {question?.questionType === 'drag' && !!question?.question && !!question?.dAnswer && (
              <>
                <FormGroup>
                  <Dnd
                    ref={(el) => {
                      if (el) {
                        dndref.current[0] = el;
                      }
                    }}
                    taskId='dnd-1'
                    number={questionNumber}
                    previouslyWrong={previouslyWrong}
                    title=''
                    answers={question?.dAnswer.map((e: string, i: number) => ({ id: i, value: e }))}
                    setCorrectAnswer={setCorrectAnswer}
                    correctAnswer={correctAnswer}
                    dAnswers={danswers}
                    additionalAnswers={
                      (correctAnswer ?? []).length - (question?.dAnswer ?? []).length
                    }
                    haveDrag={question.question.split('###BLANK###').length > 1}
                    xAnswer={xAnswer ?? []}
                    lable1={question?.lable1}
                    lable2={question?.lable2}
                    questionHead={question?.questionHead}
                    questionImage={question?.questionImage}>
                    {ReactHtmlParser(
                      question.question
                        // .replaceAll('<span class="ql-ui" contenteditable="false"></span>', '')
                        // .replaceAll('<span class="ql-ui" contentEditable="false"></span>', '')
                        .replaceAll('contenteditable="false"', '')
                        .replaceAll('contentEditable="false"', '')
                        .replaceAll('&nbsp;&nbsp;&nbsp;&nbsp;', '\t')
                        .replaceAll('&nbsp;', ' '),
                    )}
                  </Dnd>
                </FormGroup>
              </>
            )}
            {question?.questionType !== 'drag' && question?.questionType !== 'fillText' && (
              <QuestionHead
                number={questionNumber}
                previouslyWrong={previouslyWrong}
                text={question.question}
                image={
                  !!question?.questionImage || question?.questionImage !== ''
                    ? process.env.REACT_APP_File_URL + question.questionImage
                    : undefined
                }
                type={question.questionType === 'math' ? 'math' : question.questionContentType}
                lable1={question.lable1}
                lable2={question?.lable2}
              />
            )}
            {question?.questionType === 'math' && (
              <div style={{ marginLeft: 'calc( 10% + 24px )', width: '20%' }}>
                <TextField
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    style: { textAlign: 'end' },
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                  style={{
                    marginTop: -10,
                    marginBottom: 30,
                  }}
                  value={correctAnswer?.[0] ?? 0}
                  label=''
                  id='answer'
                  name='answer'
                  autoComplete='off'
                  onFocus={(event) => {
                    event.target.setAttribute('autocomplete', 'off');
                  }}
                  disabled={wrong}
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/[^0-9,^.]/g, '');
                    if (onlyNums.split('.').length < 3) {
                      if (onlyNums.length < 10) {
                        setCorrectAnswer([onlyNums + '']);
                      } else if (onlyNums.length === 10) {
                        const number = onlyNums.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                        setCorrectAnswer([number + '']);
                      }
                    }
                  }}
                />
              </div>
            )}
            <FormGroup>
              <Grid container style={{ flexWrap: 'nowrap' }}>
                {['text', 'textArea', 'number', 'math'].includes(question.questionType) && (
                  <TTSText text={spokenAnswer(question.questionType, correctAnswer?.[0])} hideText={true} />
                )}
                {question.questionType === 'boolean' && (
                  <RadioGroup
                    row
                    aria-labelledby='demo-radio-buttons-group-label'
                    value={correctAnswer?.[0]}
                    defaultChecked={true}
                    id='answer'
                    name='answer'
                    onChange={(e) => {
                      setCorrectAnswer([e.target.value]);
                    }}>
                    <FormControlLabel
                      value='true'
                      control={<Radio disabled={wrong} />}
                      label='True'
                    />
                    <FormControlLabel
                      value='false'
                      control={<Radio disabled={wrong} />}
                      label='False'
                    />
                  </RadioGroup>
                )}
                {question.questionType === 'text' &&
                  (answerWidth < 900 ? (
                    <TextField
                      style={{
                        textAlign: 'left',
                        marginTop: 30,
                        width: +(answerWidth + 28) + 'px',
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                      value={correctAnswer?.[0]}
                      id='answer'
                      name='answer'
                      autoComplete='off'
                      onFocus={(event) => {
                        event.target.setAttribute('autocomplete', 'off');
                      }}
                      disabled={wrong}
                      onChange={(e) => {
                        setCorrectAnswer([e.target.value]);
                      }}
                    />
                  ) : (
                    <TextField
                      multiline
                      rows={Math.ceil(answerWidth / 900)}
                      fullWidth
                      style={{ textAlign: 'left', marginTop: 30 }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                      value={correctAnswer?.[0]}
                      label='Answer'
                      id='answer'
                      name='answer'
                      autoComplete='off'
                      onFocus={(event) => {
                        event.target.setAttribute('autocomplete', 'off');
                      }}
                      disabled={wrong}
                      onChange={(e) => {
                        setCorrectAnswer([e.target.value]);
                      }}
                    />
                  ))}
                {question.questionType === 'textArea' && (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    style={{ textAlign: 'left', marginTop: 30 }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                    value={correctAnswer?.[0]}
                    label='Answer'
                    id='answer'
                    name='answer'
                    autoComplete='off'
                    onFocus={(event) => {
                      event.target.setAttribute('autocomplete', 'off');
                    }}
                    disabled={wrong}
                    onChange={(e) => {
                      setCorrectAnswer([e.target.value]);
                    }}
                  />
                )}
                {question.questionType === 'number' && (
                  <TextField
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                    style={{ textAlign: 'left', marginTop: 30, width: +(answerWidth + 28) + 'px' }}
                    value={correctAnswer?.[0] ?? 0}
                    id='answer'
                    name='answer'
                    autoComplete='off'
                    onFocus={(event) => {
                      event.target.setAttribute('autocomplete', 'off');
                    }}
                    disabled={wrong}
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/[^0-9,^.]/g, '');
                      if (onlyNums.split('.').length < 3) {
                        if (onlyNums.length < 10) {
                          setCorrectAnswer([onlyNums + '']);
                        } else if (onlyNums.length === 10) {
                          const number = onlyNums.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                          setCorrectAnswer([number + '']);
                        }
                      }
                    }}
                  />
                )}
              </Grid>
              {question.questionType === 'multipleSelect' && (
                <Grid container spacing={2}>
                  {question.answer.map((e: any, i: number) => (
                    <Answer
                      key={i}
                      number={i + 1}
                      text={e.answer}
                      cType={e.answerContentType}
                      image={
                        // eslint-disable-next-line
                        e?.answerImage !== undefined || e?.answerImage !== ''
                          ? e?.answerImage
                          : undefined
                      }
                      value={!!(correctAnswer ?? []).find((e) => e === i + '')}
                      type={
                        <Checkbox
                          disabled={wrong}
                          checked={!!(correctAnswer ?? []).find((e) => e === i + '')}
                          onChange={(e) => {
                            setCorrectAnswer(
                              e.target.value === 'false'
                                ? [...(correctAnswer ?? []), i + '']
                                : (correctAnswer ?? []).filter((x) => x !== i + ''),
                            );
                          }}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                          style={{ paddingTop: '0px' }}
                        />
                      }
                    />
                  ))}
                </Grid>
              )}
            </FormGroup>
            {question?.questionType === 'select' && (
              <RadioGroup
                aria-labelledby='demo-radio-buttons-group-label'
                value={correctAnswer?.[0]}
                onChange={(e) => setCorrectAnswer([(e.target as HTMLInputElement).value])}
                name='radio-buttons-group'>
                <Grid container spacing={2}>
                  {question.answer.map((e: any, i: number) => (
                    <Answer
                      key={i}
                      number={i + 1}
                      value={i}
                      text={e.answer}
                      cType={e.answerContentType}
                      image={
                        // eslint-disable-next-line
                        e?.answerImage !== undefined || e?.answerImage !== ''
                          ? e?.answerImage
                          : undefined
                      }
                      type={<Radio disabled={wrong} style={{ paddingTop: '2px' }} />}
                    />
                  ))}
                </Grid>
              </RadioGroup>
            )}
            {question?.questionType === 'new-select' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 20 }}>
                  <TextField
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                    style={{
                      width: Math.max(
                        (findMinWidth(question?.dAnswer ?? [])?.width ?? 20) + 48,
                        210,
                      ),
                    }}
                    value={correctAnswer?.[0] ?? ''}
                    id='answer-new-select'
                    name='answer-new-select'
                    autoComplete='off'
                    onFocus={(event) => {
                      event.target.setAttribute('autocomplete', 'off');
                    }}
                    disabled={wrong}
                    inputProps={{ readOnly: true }}
                    placeholder='Click an answer below'
                  />
                </div>
                <div style={{ marginTop: 10 }}>
                  <ClickWordBank
                    items={question?.dAnswer ?? []}
                    onSelect={(v: string) => setCorrectAnswer([v])}
                    minW={(findMinWidth(question?.dAnswer ?? [])?.width ?? 20) + 48}
                    label={'Click an answer to fill the blank'}
                  />
                </div>
                <TTSText text={spokenAnswer('new-select', correctAnswer?.[0])} />
              </>
            )}
            {wrong && (
              <Button
                color={'success'}
                variant='contained'
                onClick={goNext}
                sx={{
                  width: '150px',
                  fontSize: '16px',
                  borderRadius: 10,
                  margin: '0 0 auto auto',
                }}>
                Next
              </Button>
            )}
            {!wrong && (
              <Button
                color={'primary'}
                variant='contained'
                disabled={loading1}
                onClick={() => submit(questionId)}
                sx={{
                  width: '150px',
                  fontSize: '16px',
                  borderRadius: 10,
                  margin: '0 0 auto auto',
                }}>
                submit
              </Button>
            )}
            {wrong && (
              <Grid item sm={12} style={{ marginBottom: '20px' }}>
                <Typography
                  variant='h3'
                  color={'#35a845'}
                  sx={{ fontWeight: 700, marginTop: '5px', marginBottom: '5px', fontSize: '18px' }}>
                  Correct Answer
                </Typography>
                <FormGroup>
                  <Grid container style={{ flexWrap: 'nowrap' }}>
                    {['text', 'textArea', 'number'].includes(question.questionType) && (
                      <TTSText hideText={true} text={spokenAnswer(question.questionType, correctAnswer1?.[0])} />
                    )}
                    {question.questionType === 'boolean' && (
                      <RadioGroup
                        row
                        aria-labelledby='demo-radio-buttons-group-label'
                        value={correctAnswer1?.[0]}
                        defaultChecked={true}
                        id='answer'
                        name='answer'
                        onChange={(e) => {
                          //
                        }}>
                        <FormControlLabel value='true' control={<Radio />} label='True' />
                        <FormControlLabel value='false' control={<Radio />} label='False' />
                      </RadioGroup>
                    )}
                    {question.questionType === 'text' &&
                      (answerWidth < 900 ? (
                        <TextField
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                          style={{
                            textAlign: 'left',
                            marginTop: 30,
                            width: +(answerWidth + 28) + 'px',
                          }}
                          value={correctAnswer1?.[0]}
                          id='answer'
                          name='answer'
                          onChange={() => {
                            //
                          }}
                        />
                      ) : (
                        <TextField
                          multiline
                          rows={Math.ceil(answerWidth / 900)}
                          fullWidth
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                          style={{ textAlign: 'left', marginTop: 30 }}
                          value={correctAnswer1?.[0]}
                          label='Answer'
                          id='answer'
                          name='answer'
                          onChange={() => {
                            //
                          }}
                        />
                      ))}
                    {question.questionType === 'textArea' && (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                        style={{ textAlign: 'left', marginTop: 30 }}
                        value={correctAnswer1?.[0]}
                        label='Answer'
                        id='answer'
                        name='answer'
                        onChange={() => {
                          //
                        }}
                      />
                    )}
                    {question.questionType === 'number' && (
                      <TextField
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        style={{
                          textAlign: 'left',
                          marginTop: 30,
                          width: +(answerWidth + 28) + 'px',
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                        value={correctAnswer1?.[0]}
                        id='answer'
                        name='answer'
                        onChange={() => {
                          //
                        }}
                      />
                    )}
                  </Grid>
                  {question.questionType === 'multipleSelect' && (
                    <Grid container spacing={2}>
                      {question.answer.map((e: any, i: number) => (
                        <Answer
                          key={i}
                          number={i + 1}
                          text={e.answer}
                          cType={e.answerContentType}
                          image={
                            // eslint-disable-next-line
                            e?.answerImage !== undefined || e?.answerImage !== ''
                              ? e?.answerImage
                              : undefined
                          }
                          value={!!(correctAnswer1 ?? []).find((e) => e === i + '')}
                          type={
                            <Checkbox
                              checked={!!(correctAnswer1 ?? []).find((e) => e === i + '')}
                              onChange={() => {
                                //
                              }}
                              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                              style={{ paddingTop: '2px' }}
                            />
                          }
                        />
                      ))}
                    </Grid>
                  )}
                </FormGroup>
                {question.questionType === 'select' && (
                  <RadioGroup
                    aria-labelledby='demo-radio-buttons-group-label'
                    value={correctAnswer1?.[0]}
                    onChange={() => {
                      //
                    }}
                    name='radio-buttons-group'>
                    <Grid container spacing={2}>
                      {question.answer.map((e: any, i: number) => (
                        <Answer
                          key={i}
                          number={i + 1}
                          value={i}
                          text={e.answer}
                          cType={e.answerContentType}
                          image={
                            // eslint-disable-next-line
                            e?.answerImage !== undefined || e?.answerImage !== ''
                              ? e?.answerImage
                              : undefined
                          }
                          type={<Radio style={{ paddingTop: '2px' }} />}
                        />
                      ))}
                    </Grid>
                  </RadioGroup>
                )}
                {question.questionType === 'new-select' && (
                  <TextField
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                    style={{ textAlign: 'left', marginTop: 30, width: '300px' }}
                    value={correctAnswer1?.[0] ?? ''}
                    id='answer-new-select-correct'
                    name='answer-new-select-correct'
                    onChange={() => {
                      /* no-op */
                    }}
                  />
                )}
                {question.questionType === 'new-select' && (
                  <TTSText text={spokenAnswer('new-select', correctAnswer1?.[0])} />
                )}
              </Grid>
            )}
            {wrong && question?.questionType === 'math' && (
              <>
                <QuestionHead
                  number={questionNumber}
                  previouslyWrong={previouslyWrong}
                  text={question.question}
                  image={
                    !!question?.questionImage || question?.questionImage !== ''
                      ? process.env.REACT_APP_File_URL + question.questionImage
                      : undefined
                  }
                  type={question.questionType === 'math' ? 'math' : question.questionContentType}
                />
                <div style={{ marginLeft: 'calc( 10% + 24px )', width: '20%' }}>
                  <TextField
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: { textAlign: 'end' },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '16px' } }}
                    style={{
                      marginTop: -10,
                      marginBottom: 30,
                    }}
                    value={correctAnswer1?.[0]}
                    id='answer'
                    name='answer'
                    onChange={(e) => {
                      //
                    }}
                  />
                </div>
                <TTSText text={spokenAnswer('math', correctAnswer1?.[0])} />
              </>
            )}
            {wrong && question?.questionType === 'drag' && (
              <CorrectDnd
                answers={correctAnswer1}
                xAnswer={xAnswer ?? []}
                isCorrectByBlank={reviewIsCorrectByBlank}>
                {ReactHtmlParser(
                  question.question
                    // .replaceAll('<span class="ql-ui" contenteditable="false"></span>', '')
                    // .replaceAll('<span class="ql-ui" contentEditable="false"></span>', '')
                    .replaceAll('contenteditable="false"', '')
                    .replaceAll('contentEditable="false"', '')
                    .replaceAll('&nbsp;&nbsp;&nbsp;&nbsp;', '\t')
                    .replaceAll('&nbsp;', ' '),
                )}
              </CorrectDnd>
            )}
            {wrong && question?.questionType === 'fillText' && (
              <CorrectFillText answers={correctAnswer1} isCorrectByBlank={reviewIsCorrectByBlank}>
                {ReactHtmlParser(
                  question.question
                    // .replaceAll('<span class="ql-ui" contenteditable="false"></span>', '')
                    // .replaceAll('<span class="ql-ui" contentEditable="false"></span>', '')
                    .replaceAll('contenteditable="false"', '')
                    .replaceAll('contentEditable="false"', '')
                    .replaceAll('&nbsp;&nbsp;&nbsp;&nbsp;', '\t')
                    .replaceAll('&nbsp;', ' '),
                )}
              </CorrectFillText>
            )}
            <Snackbar open={open} autoHideDuration={2000} onClose={handleClose}>
              {/* <Alert onClose={handleClose} severity='success' sx={{ width: '100%' }}>*/}
              {/*  This is a success message!*/}
              {/* </Alert>*/}
              <div>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore*/}
                <Alert onClose={handleClose} severity='success' sx={{ width: '100%' }}>
                  You Answer Is Correct!
                </Alert>
              </div>
            </Snackbar>
            <Snackbar open={open2} autoHideDuration={2000} onClose={() => setOpen2(false)}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <Alert onClose={() => setOpen2(false)} severity='warning' sx={{ width: '100%' }}>
                Please enter an answer first
              </Alert>
            </Snackbar>
            <Snackbar open={open1} autoHideDuration={2000} onClose={handleClose1}>
              {/* <Alert onClose={handleClose} severity='success' sx={{ width: '100%' }}>*/}
              {/*  This is a success message!*/}
              {/* </Alert>*/}
              <div>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore*/}
                <Alert onClose={handleClose1} severity='error' sx={{ width: '100%' }}>
                  You Answer Is Wrong!
                </Alert>
              </div>
            </Snackbar>
          </FormControl>
        </div>
      )}
    </>
  );
};

export const QuestionSet: FC<{ setX: (x: any) => void }> = ({ setX }) => {
  const timerRef = useRef<any>();
  const connection = useConnection();
  const navigator = useNavigate();
  const params = useParams<{ subTitleId: string }>();
  const [subTitle, setSubTitle] = useState('');
  const [subTitlePdf, setSubTitlePdf] = useState<string | undefined>();
  const [seconds, setSeconds] = useState(0);
  const [mainTitle, setMainTitle] = useState({ value: '', error: false, orderNum: 0, pdf: '' });
  const [category, setCategory] = useState({ id: '', value: '' });
  const [grade, setGrade] = useState({ id: '', value: '' });
  const [pdf, setPdf] = useState<string | undefined>();
  const [questions, setQuestions] = useState<any[]>([]);
  const [mark, setMark] = useState(0);
  const [current, setCurrent] = useState(0);
  const [indexCurrent, setIndexCurrent] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [initialDoneQuestionCount, setInitialDoneQuestionCount] = useState(0);
  const [doneQuestionCount, setDoneQuestionCount] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [attemptedCount, setAttemptedCount] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [qCount, setQCount] = useState(0);
  const user = useUserInfo();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const ddRef = useRef<number>(0);
  // `reload` is captured by `submit`, so it has to keep a stable identity. Read
  // the live `current` through a ref instead of listing it as a dependency.
  const currentRef = useRef(0);
  currentRef.current = current;
  const exhaustedReloadRef = useRef(false);

  useEffect(() => {
    if (!user) {
      navigator('/login');
    }
  }, [user]);

  const getSeconds = useCallback((x: number) => {
    setSeconds(x);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-empty
    if (indexCurrent >= questions.length && indexCurrent !== 0) {
      timerRef.current?.toggle(false);
    }
  }, [indexCurrent, questions]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!params.subTitleId) {
      navigator('/');
    } else {
      setLoading(true);
      connection.get(`sub-title/questions/${params.subTitleId}`).then(async (e) => {
        if (e.response.timeOut) {
          await Swal.fire({
            title: 'Warning!',
            text: 'Your Free Trial Time Is Over. Please Try Again Next Day',
            icon: 'warning',
            confirmButtonText: 'Ok',
          });
          navigator('/');
        }
        if (e.response.data.length === 0) {
          navigator('/');
        } else {
          setSubTitle(e.response.data[0].subTitle.value);
          setSubTitlePdf(e.response.data[0].subTitle?.pdf);
          ++ddRef.current;
          if (subTitle !== e.response.data[0].subTitle.value && ddRef.current === 1) {
            setMainTitle(e.response.data[0].mainTitle);
            setGrade(e.response.data[0].grade);
            setCategory(e.response.data[0].category);
            setX({
              mainTitle: e.response.data[0].mainTitle,
              grade: e.response.data[0].grade,
              category: e.response.data[0].category,
            })
            setPdf(e.response.data[0].mainTitle?.pdf);
            setCurrent(0);
            setIndexCurrent(0)
            setQCount(0);
            setMark(0);
            setQuestionCount(e.response?.questionCount ?? 0);
            setDoneQuestionCount(e.response?.doneQuestionCount ?? 0);
            setInitialDoneQuestionCount(e.response?.doneQuestionCount ?? 0);
            if (typeof e.response?.attemptedCount === 'number') {
              setAttemptedCount(e.response.attemptedCount);
            }
            setQuestions(e.response.data);
          }
        }
        timerRef.current?.toggle(true);
        setLoading(false);
      });
    }
  }, []);

  const reload = useCallback(async () => {
    await connection.get(`sub-title/questions/${params.subTitleId}`).then(async (e) => {
      if (e.response.timeOut) {
        await Swal.fire({
          title: 'Warning!',
          text: 'Your Free Trial Time Is Over. Please Try Again Next Day',
          icon: 'warning',
          confirmButtonText: 'Ok',
        });
        navigator('/');
      }
      if (e.response.data.length === 0) {
        navigator('/');
      } else {
        ++ddRef.current;
        setDoneQuestionCount(e.response?.doneQuestionCount ?? 0);
        setQuestionCount(e.response?.questionCount ?? 0);
        setInitialDoneQuestionCount((e.response?.doneQuestionCount ?? 0) - currentRef.current);
        if (typeof e.response?.attemptedCount === 'number') {
          setAttemptedCount(e.response.attemptedCount);
        }
        setQuestions(e.response.data);
        setIndexCurrent(0);
      }
      setLoading(false);
    });
  }, [connection, params.subTitleId, navigator]);

  useEffect(() => {
    // The local batch ran out while the subtitle is still unfinished, so missed
    // questions are waiting on the server. Without this refetch neither the
    // question nor the "Go Home" button renders and the screen goes blank.
    if (loading || questions.length === 0 || indexCurrent < questions.length) {
      exhaustedReloadRef.current = false;
      return;
    }
    if (questionCount > 0 && doneQuestionCount >= questionCount) {
      return;
    }
    if (exhaustedReloadRef.current) {
      return;
    }
    exhaustedReloadRef.current = true;
    setLoading(true);
    reload();
  }, [loading, questions, indexCurrent, doneQuestionCount, questionCount, reload]);

  return (
    <Grid
      container
      spacing={2}
      direction={matches ? 'column' : 'row'}
      style={{ paddingTop: '100px' }}>
      <Grid
        item
        sm={12}
        md
        style={{ position: 'relative', width: matches ? '100%' : 'calc(100% - 185px)' }}>
        {/* <Grid item sm={12} style={{ display: 'flex', alignItems: 'center' }}>*/}
        {/*    <Typography variant='h3' sx={{ fontWeight: 700, fontSize: '28px' }}>*/}
        {/*      {mainTitle.value}*/}
        {/*    </Typography>*/}
        {/*    {pdf && (*/}
        {/*      <a href={pdf} target='_blank' rel='noreferrer'>*/}
        {/*        <img src={'/pdf.png'} style={{ width: '30px', paddingLeft: '10px' }} />*/}
        {/*      </a>*/}
        {/*    )}*/}
        {/*  </Grid>*/}
        <Grid item sm={12} style={{ display: 'flex', alignItems: 'center', paddingLeft: '15px' }}>
          <Typography variant='h5' sx={{ fontWeight: 700, fontSize: '18px' }}>
            {subTitle}{' '}
            {subTitle && (
              <span
                style={{
                  color: '#575757',
                  fontWeight: '400',
                  fontSize: '11px',
                  marginLeft: '5px',
                }}>
                {doneQuestionCount} / {questionCount}
              </span>
            )}
          </Typography>
          {!!pdf && pdf !== '' && (
            <a href={pdf} target='_blank' rel='noreferrer'>
              <img src={'/pdf.svg'} alt='main title pdf' style={{ width: '28px', marginLeft: '8px' }} />
            </a>
          )}
          {!!subTitlePdf && subTitlePdf !== '' && (
            <a href={subTitlePdf} target='_blank' rel='noreferrer'>
              <img src={'/pdf.svg'} alt='sub title pdf' style={{ width: '28px', marginLeft: '8px' }} />
            </a>
          )}
        </Grid>
        {!loading && questions.length !== 0 && indexCurrent < questions.length && (
          <Question
            current={current}
            initialDoneQuestionCount={initialDoneQuestionCount}
            indexCurrent={indexCurrent}
            currentCount={currentCount}
            mark={mark}
            question={{
              ...questions[indexCurrent].question,
              xAnswer: questions[indexCurrent],
              dAnswer: questions[indexCurrent]?.answer,
            }}
            questionId={questions[indexCurrent].id}
            setMark={setMark}
            time={seconds}
            setCurrent={setCurrent}
            setIndexCurrent={setIndexCurrent}
            setCurrentCount={setCurrentCount}
            setQCount={setQCount}
            setDoneQuestionCount={setDoneQuestionCount}
            qCount={qCount}
            answerLength={+(questions[indexCurrent]?.answerLength ?? 1)}
            previouslyWrong={!!questions[indexCurrent]?.previouslyWrong}
            previousQuestionNumber={questions[indexCurrent]?.previousQuestionNumber ?? null}
            attemptedCount={attemptedCount}
            setAttemptedCount={setAttemptedCount}
            xAnswer={questions?.[indexCurrent]?.xAnswer ?? ['BB']}
            reload={reload}
          />
        )}
        {!loading && questionCount > 0 && doneQuestionCount >= questionCount && (
          <Button
            color={'primary'}
            variant='contained'
            onClick={() => navigator('/')}
            sx={{
              width: '150px',
              fontSize: '16px',
              borderRadius: 10,
              margin: '60px 0 auto auto',
            }}>
            Go Home
          </Button>
        )}
      </Grid>
      <Grid
        item
        sm={12}
        md
        style={{
          maxWidth: matches ? '100%' : '185px',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: '50px',
        }}>
        <Timer ref={timerRef} mark={Math.round(mark)} getSeconds={getSeconds} qNumber={qCount} />
      </Grid>
    </Grid>
  );
};
